import { useCallback } from 'react';
import { useConfig } from '@lore/config';

/**
 * Low-level fetch helper for unauthenticated public API endpoints.
 * Throws on non-2xx responses so react-query can treat them as errors.
 *
 * @param {string} url - Fully-qualified endpoint URL.
 * @param {RequestInit} [options] - Additional fetch options (method, headers, body, …).
 * @returns {Promise<any>} Parsed JSON response.
 */
export function fetchPublic(url, options = {}) {
  return fetch(url, { method: 'GET', ...options }).then((res) => {
    if (!res.ok) {
      throw new Error(`Public API request failed: ${res.status} ${res.statusText}`);
    }
    return res.json();
  });
}

/**
 * Returns a stable `revalidate` callback that repopulates the browser HTTP
 * cache for `url` without blocking the caller.
 *
 * 'reload' fetches from the network and writes the result to the cache;
 * 'no-store' would fetch but skip the write, leaving the stale entry.
 *
 * @param {string} url
 * @returns {Function}
 */
export function usePublicRevalidate(url) {
  return useCallback(() => {
    fetch(url, { cache: 'reload' }).catch(() => {});
  }, [url]);
}

/**
 * Builds the base URL for a public API path using the configured apiRoot.
 *
 * @returns {string}
 */
export function useApiRoot() {
  const config = useConfig();
  return config.connections.default.apiRoot;
}
